import { useEffect, useState, useRef } from "react";
import { LayoutComponent } from "../LayoutComponent";
import {
  Flex,
  Heading,
  Content,
  View,
  Link,
  ProgressCircle,
  DialogContainer,
  Dialog,
} from "@adobe/react-spectrum";
import moment from "moment";
import { formatDecimalToString } from "../../../../utils/helperUtil";
import ProjectProductAddEdit, {
  AttributeData,
} from "../ProjectProduct/ProjectProductAddEdit";
import ProductSelectionModal, {
  SelectedProduct,
} from "../../../ui/ProductSelectionModal";
import { useDependency } from "../../../../contexts/DependencyProvider";
import { ListProjectItemRequest } from "../../../../services/soap/item/requests/ListProjectItemRequest";
import { ListProjectItemRequestOptions_ProjectItem } from "../../../../services/soap/item/requests/ListProjectItemRequestOptions";
import { useViewInfo } from "../../../../hooks/UseViewInfo";
import { useTranslation } from "react-i18next";
import { FPAData, FPADataTypes } from "../../../../infra/protected/FPA/FPAData";
import { UpdateProjectItemRequest } from "../../../../services/soap/item/requests/UpdateProjectItemRequest";
import { generateProjectProductItem } from "../../../../services/soap/item/requests/CreateProjectItemOptions";
import { ToastQueue } from '@adobe/react-spectrum';
import { GetItemRequest } from "../../../../services/soap/item/requests/GetItemRequest";
import { CreateProjectItemRequest } from "../../../../services/soap/item/requests/CreateProjectItemRequest";
import { DeleteProjectItemRequest } from "../../../../services/soap/item/requests/DeleteProjectItemRequest";
import { useTableViewHeight } from "../../../../utils/tableViewUtils";
import styles from "../ProjectProduct/project_product_component.module.css";
import { TreeGridComponent } from "../../../ui/TreeGridComponent/TreeGridComponent";
import { useItemAttributesCache } from "../../../../hooks/useItemAttributesCache";
import {
  TreeGridRow,
  TreeGridColumn,
} from "../../../ui/TreeGridComponent/types";
import { moveRow } from "../../../ui/TreeGridComponent/utils/treeOperations";
import {
  AttributesCacheContext,
  ProjectProductAttributeCellEditor,
} from "./ProjectProductAttributeCellEditor";

interface ProjectProductTreeProps {
  attrib0ColumnName?: string;
  attrib1ColumnName?: string;
  attrib2ColumnName?: string;
  attrib0ColumnVisible?: boolean;
  attrib1ColumnVisible?: boolean;
  attrib2ColumnVisible?: boolean;
  billingDateColumnName?: string;
  billingDateColumnVisible?: boolean;
  componentTitle?: string;
  minHeight?: number;
  percentWidth?: number;
  productType?: number;
  quantityColumnName?: string;
  quantityColumnVisible?: boolean;
  serialNumberColumnName?: string;
  serialNumberColumnVisible?: boolean;
  serialNumberComponentIdent?: string;
  unitPriceColumnName?: string;
  unitPriceColumnVisible?: boolean;
  supplierColumnVisible?: boolean;
  selectedItem?: FPAData;
  isNew?: boolean;
  setData?: (dataItems: any[]) => void;
  isRequired?: boolean; // need for Create New Context
}

export interface ProjectProductTreeRowData {
  id: string;
  quantity: number;
  partID: string;
  _refId?: string;
  supplier?: string;
  supplierName?: string;
  partNumber: string;
  productName: string;
  currency: string;
  unitPrice: number;
  amountWithVat: number;
  vatValue: number;
  totalPrice: string;
  totalPriceWithVat: string;
  attrib0: string;
  attrib0_id: string;
  attrib1: string;
  attrib1_id: string;
  attrib2: string;
  attrib2_id: string;
  refId: string;
  itemSerial?: string;
  totalQuantity: number;
  amount: number;
  billingDate: string;
  itemType?: string;
  purchasePrice?: number;
  totalAmountWithVat?: number;
  totalPriceCurr?: string;
  unitPriceCurr?: string;
  billingDateFormatted?: string;
  // Tree metadata
  parentId?: string | null;
  childrenIds?: string[];
  depth?: number;
  isExpanded?: boolean;
  sortOrder?: number;
}

function ProjectProductTree({
  attrib0ColumnName = "Col.0",
  attrib1ColumnName = "Col.1",
  attrib2ColumnName = "Col.2",
  attrib0ColumnVisible = false,
  attrib1ColumnVisible = false,
  attrib2ColumnVisible = false,
  billingDateColumnName = "Billing Date",
  billingDateColumnVisible = false,
  componentTitle = "Project products",
  minHeight = 64,
  percentWidth = 100,
  productType = 2,
  quantityColumnName = "Quantity",
  quantityColumnVisible = false,
  serialNumberColumnName = "Serial Number",
  serialNumberColumnVisible = false,
  serialNumberComponentIdent = "",
  unitPriceColumnName = "Unit Price",
  unitPriceColumnVisible = false,
  supplierColumnVisible = false,
  selectedItem,
  isNew,
  setData,
  isRequired,
}: ProjectProductTreeProps) {
  const { t } = useTranslation();

  const defaultRowData: ProjectProductTreeRowData = {
    id: "0",
    quantity: 1,
    partID: "",
    _refId: "",
    partNumber: "",
    productName: "",
    currency: "",
    unitPrice: 0,
    amountWithVat: 0,
    vatValue: 0,
    totalPrice: "0",
    totalPriceWithVat: "0",
    attrib0: "",
    attrib0_id: "",
    attrib1: "",
    attrib1_id: "",
    attrib2: "",
    attrib2_id: "",
    refId: "",
    totalQuantity: 0,
    amount: 0,
    supplier: "",
    supplierName: "",
    billingDate: moment().toISOString(),
    billingDateFormatted: moment().format("MM/DD/YYYY"),
    purchasePrice: 0,
    totalAmountWithVat: 0,
    totalPriceCurr: "0",
    unitPriceCurr: "0",
    parentId: null,
    childrenIds: [],
    depth: 0,
    isExpanded: true,
    itemSerial: "",
  };

  const { itemService, store } = useDependency();
  const { isMobile } = useViewInfo();

  // Use the custom hook for attributes cache management
  const {
    attributesCache,
    getAttributes,
    ensureAttributesForType,
    loadAttributesForTypes,
  } = useItemAttributesCache(itemService, store);

  const [projectProductData, setProjectProductData] = useState<any>([]);
  const [rowsData, setRowsData] = useState<ProjectProductTreeRowData[]>([]);
  const [selectedRow, setSelectedRow] =
    useState<ProjectProductTreeRowData>(defaultRowData);
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const [showAddEditRow, setShowAddEditRow] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isProductSelectionModalOpen, setIsProductSelectionModalOpen] =
    useState<boolean>(false);
  const [treeGridKey, setTreeGridKey] = useState(0);
  const isSavingProducts = useRef<boolean>(false);

  // Use the utility for dynamic height calculations (affects container styling)
  const { tableHeight, containerHeight, tableStyles } = useTableViewHeight(
    rowsData.length,
    {
      headerHeight: 50,
      rowHeight: 50,
      maxVisibleRows: 5,
      containerExtraSpace: 0,
      maxContainerHeight: 550,
      showOverflow: true,
    },
  );

  const [columns, setColumns] = useState<
    TreeGridColumn<ProjectProductTreeRowData>[]
  >([
    {
      key: "productName",
      header: t("product", { ns: "layout_components" }),
      width: 250,
      isTreeColumn: true,
      showFolderIcon: true,
      editable: true,
      editorType: "text",
    },
    {
      key: "partNumber",
      header: t("product_code", { ns: "layout_components" }),
      width: 120,
    },
    {
      key: "supplierName",
      header: t("supplier", { ns: "layout_components" }),
      width: 200,
    },
    {
      key: "attrib0",
      header: attrib0ColumnName,
      width: 120,
      editable: true,
      editorType: "custom",
      customEditor: ProjectProductAttributeCellEditor,
    },
    {
      key: "attrib1",
      header: attrib1ColumnName,
      width: 120,
      editable: true,
      editorType: "custom",
      customEditor: ProjectProductAttributeCellEditor,
    },
    {
      key: "attrib2",
      header: attrib2ColumnName,
      width: 120,
      editable: true,
      editorType: "custom",
      customEditor: ProjectProductAttributeCellEditor,
    },
    {
      key: "refId",
      header: t("ref_id", { ns: "layout_components" }),
      width: 120,
      editable: true,
      editorType: "text",
    },
    {
      key: "itemSerial",
      header: serialNumberColumnName,
      width: 120,
      editable: true,
      editorType: "text",
    },
    {
      key: "quantity",
      header: quantityColumnName,
      width: 100,
      align: "right",
      editable: true,
      editorType: "number",
      numberFormat: { decimals: 2 },
    },
    {
      key: "unitPrice",
      header: unitPriceColumnName,
      width: 150,
      align: "right",
      editable: true,
      editorType: "number",
      numberFormat: { decimals: 2 },
      format: (val: number, row: TreeGridRow<ProjectProductTreeRowData>) =>
        row.data?.unitPriceCurr ?? "",
    },
    {
      key: "billingDateFormatted",
      header: billingDateColumnName,
      width: 120,
    },
  ]);

  useEffect(() => {
    // Apply column visibility using hidden flag
    setColumns((prev) => {
      const updated = prev.map((col) => {
        if (col.key === "supplierName") {
          return { ...col, hidden: !supplierColumnVisible };
        }
        if (col.key === "attrib0") {
          return { ...col, hidden: !attrib0ColumnVisible };
        }
        if (col.key === "attrib1") {
          return { ...col, hidden: !attrib1ColumnVisible };
        }
        if (col.key === "attrib2") {
          return { ...col, hidden: !attrib2ColumnVisible };
        }
        if (col.key === "refId") {
          return { ...col, hidden: !serialNumberColumnVisible };
        }
        if (col.key === "quantity") {
          return { ...col, hidden: !quantityColumnVisible };
        }
        if (col.key === "unitPrice") {
          return { ...col, hidden: !unitPriceColumnVisible };
        }
        if (col.key === "billingDateFormatted") {
          return { ...col, hidden: !billingDateColumnVisible };
        }
        return col;
      });
      return updated;
    });
  }, [
    attrib0ColumnVisible,
    attrib1ColumnVisible,
    attrib2ColumnVisible,
    serialNumberColumnVisible,
    quantityColumnVisible,
    unitPriceColumnVisible,
    billingDateColumnVisible,
    supplierColumnVisible,
    componentTitle,
    productType,
  ]);

  useEffect(() => {
    setShowAddEditRow(false);
    (async () => {
      setShowLoader(true);
      !isNew && (await loadProjectProductData());
      setShowLoader(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem, productType]);

  const setRecordIds = (record: any, selectedItem: any) => {
    switch (selectedItem?.type) {
      case FPADataTypes.ACTIVITY:
        record.folderId = selectedItem?.parent?.parent_id;
        record.projectId = String(selectedItem?.parent_id);
        record.activityId = String(selectedItem?.id);
        break;
      case FPADataTypes.PROJECT:
        record.folderId = "";
        record.projectId = String(selectedItem?.id);
        record.activityId = "";
        break;
      case FPADataTypes.FOLDER:
        record.folderId = String(selectedItem?.id);
        record.projectId = "";
        record.activityId = "";
        break;
    }
  };

  const handleLoadAttributes = async (
    itemType: string,
  ): Promise<AttributeData[]> => {
    return ensureAttributesForType(itemType);
  };

  const buildTreeMetadata = (
    items: ProjectProductTreeRowData[],
  ): ProjectProductTreeRowData[] => {
    if (!items || items.length === 0) {
      return [];
    }

    const byId: Record<string, ProjectProductTreeRowData> = {};
    const childrenMap: Record<string, string[]> = {};

    items.forEach((item) => {
      const id = String(item.id);
      const parentId =
        item.parentId && item.parentId !== "" ? String(item.parentId) : null;

      byId[id] = {
        ...item,
        id,
        parentId,
        childrenIds: item.childrenIds ?? [],
        depth: item.depth ?? 0,
        isExpanded: item.isExpanded ?? true,
      };

      if (parentId) {
        if (!childrenMap[parentId]) {
          childrenMap[parentId] = [];
        }
        childrenMap[parentId].push(id);
      }
    });

    Object.entries(childrenMap).forEach(([parentId, childIds]) => {
      if (byId[parentId]) {
        byId[parentId].childrenIds = childIds;
      }
    });

    const computeDepth = (item: ProjectProductTreeRowData): number => {
      let depth = 0;
      let currentParentId = item.parentId;
      const visited = new Set<string>();

      while (
        currentParentId &&
        byId[currentParentId] &&
        !visited.has(currentParentId)
      ) {
        visited.add(currentParentId);
        depth += 1;
        currentParentId = byId[currentParentId].parentId ?? null;
      }

      return depth;
    };

    Object.values(byId).forEach((item) => {
      item.depth = computeDepth(item);
    });

    // Preserve original order
    return items.map((item) => byId[String(item.id)]);
  };

  // Function to load project product data and build hierarchical rows
  const loadProjectProductData = async () => {
    let record: any = {};
    setRecordIds(record, selectedItem);

    const projecrProdRes: any = await itemService.listProjectItem(
      new ListProjectItemRequest(
        store.Server,
        store.SessionId,
        50,
        0,
        new ListProjectItemRequestOptions_ProjectItem(
          record.folderId,
          record.projectId,
          record.activityId,
          String(productType),
        ),
      ),
    );

    setProjectProductData(projecrProdRes);

    if (projecrProdRes?.result === "OK") {
      let projectProductData: ProjectProductTreeRowData[] =
        projecrProdRes?.ROWS?.map((row: any) => {
          const item = row.ITEM;
          const quantityNum = Number.parseFloat(item.quantity || "0") || 0;
          const unitPriceNum = Number.parseFloat(item.amount || "0") || 0;
          const totalAmountNum =
            Number.parseFloat(item.totalAmount || "0") || 0;

          const unitPriceStr = formatDecimalToString(unitPriceNum.toFixed(2));
          const totalPriceStr = formatDecimalToString(
            totalAmountNum.toFixed(2),
          );

          const currency = item.currency || "";

          return {
            id: item.id,
            quantity: quantityNum,
            partID: item.item,
            _refId: "",
            partNumber: "Part Number",
            productName: item.name,
            supplier: item.supplier,
            supplierName: item.supplierName,
            currency,
            vatValue: Number(item.vatValue || 0),
            amountWithVat: Number(item.amountWithVat || 0),
            unitPrice: unitPriceNum,
            unitPriceCurr: `${unitPriceStr} ${currency}`,
            amount: Number(item.amount || 0),
            totalPrice: totalPriceStr,
            totalPriceWithVat: formatDecimalToString(
              Number.parseFloat(item.totalAmountWithVat || "0").toFixed(2),
            ),
            totalPriceCurr: `${totalPriceStr} ${currency}`,
            purchasePrice: Number(item.purchasePrice || 0),
            totalAmountWithVat: Number(item.totalAmountWithVat || 0),
            attrib0: item.attrib0,
            attrib0_id: item.attrib0_id,
            attrib1: item.attrib1,
            attrib1_id: item.attrib1_id,
            attrib2: item.attrib2,
            attrib2_id: item.attrib2_id,
            refId: item.refId,
            itemSerial: item.itemSerial ?? "",
            totalQuantity: Number(item.totalQuantity || 0),
            billingDate: item.billingDate,
            billingDateFormatted: moment(item.billingDate).format("MM/DD/YYYY"),
            itemType: item.itemType,
            parentId: item.parentId || null,
            childrenIds: [],
            depth: 0,
            isExpanded: true,
            sortOrder: item.orderIndex ? Number(item.orderIndex) : undefined,
          };
        }) || [];

      // Extract all unique item types from the loaded data
      const uniqueItemTypes = [
        ...new Set(
          projectProductData
            .map((item) => item.itemType)
            .filter((type): type is string => Boolean(type)),
        ),
      ];

      // Load attributes for all unique item types in parallel
      if (uniqueItemTypes.length > 0) {
        await loadAttributesForTypes(uniqueItemTypes);
      }

      // Enrich with partNumber via GetItemRequest
      projectProductData = await updateProjectProductData(projectProductData);

      const withTreeMeta = buildTreeMetadata(projectProductData);
      setRowsData(withTreeMeta);
    }
  };

  const updateProjectProductData = async (
    projectProductData: ProjectProductTreeRowData[],
  ): Promise<ProjectProductTreeRowData[]> => {
    if (!projectProductData) return [];
    return Promise.all(
      projectProductData.map(async (item) => {
        const partNumber = await getPartNumber(item);
        return {
          ...item,
          _refId: `${partNumber}_${item.productName}_ref`,
          partNumber: partNumber,
        } as ProjectProductTreeRowData;
      }),
    );
  };

  const getPartNumber = async (
    item: ProjectProductTreeRowData,
  ): Promise<string> => {
    const itemResp = await itemService.getItem(
      new GetItemRequest(store.Server, store.SessionId, item.partID),
    );
    return itemResp.ITEM.refId ?? "";
  };

  const handleRowClose = (): void => {
    setSelectedRow(defaultRowData);
    setShowAddEditRow(false);
    isMobile && setIsOpen(false);
  };

  const handleSaveSelectedProducts = async (
    selectedProducts: SelectedProduct[],
  ) => {
    if (isSavingProducts.current) {
      return;
    }

    if (!selectedProducts || selectedProducts.length === 0) {
      ToastQueue.negative(
        t("no_products_selected", {
          ns: "layout_components",
          defaultValue: "No products selected",
        }),
        { timeout: 3000 },
      );
      return;
    }

    if (!selectedItem?.id) {
      ToastQueue.negative(
        t("no_project_selected", {
          ns: "layout_components",
          defaultValue: "No project selected",
        }),
        { timeout: 3000 },
      );
      return;
    }

    isSavingProducts.current = true;
    setShowLoader(true);

    let successCount = 0;
    let failureCount = 0;
    const failedProducts: string[] = [];

    try {
      const record: any = {};
      setRecordIds(record, selectedItem);

      const billingDate = new Date().toISOString().split("T")[0];

      const productPromises = selectedProducts.map(async (product) => {
        try {
          const quantity = product.quantity || 1;
          const unitPrice = product.unitPrice || 0;
          const totalPrice = quantity * unitPrice;
          const totalAmountWithVat = (product.amountWithVat || 0) * quantity;

          const result = await itemService.createProjectItem(
            new CreateProjectItemRequest(
              store.Server,
              store.SessionId,
              generateProjectProductItem(
                record.folderId || "",
                record.projectId || "",
                record.activityId || "",
                product.partID || "",
                product.productName,
                quantity,
                unitPrice,
                totalPrice,
                totalAmountWithVat,
                product.currency || "",
                String(product.vatValue || 0),
                String(product.purchasePrice || 0),
                "", // attrib0
                "", // attrib0_id
                "", // attrib1
                "", // attrib1_id
                "", // attrib2
                "", // attrib2_id
                "", // attrib3
                "", // attrib3_id
                "", // attrib4
                "", // attrib4_id
                "", // attrib5
                "", // attrib5_id
                "", // attrib6
                "", // attrib6_id
                "", // attrib7
                "", // attrib7_id
                "", // attrib8
                "", // attrib8_id
                "", // attrib9
                "", // attrib9_id
                "",
                product.partNumber || "",
                billingDate,
                "1",
                undefined,
                { type: String(productType) },
              ),
            ),
          );

          if (result.result !== "OK") {
            throw new Error(
              result.EXCEPTION?.reason ||
                t("failed_to_create_item", {
                  ns: "layout_components",
                  defaultValue: "Failed to create item",
                }),
            );
          }

          const parentItemId = result.itemId;

          if (product.components && product.components.length > 0) {
            for (const component of product.components) {
              try {
                const compQuantity = parseFloat(component.countMin) || 1;
                const compUnitPrice = parseFloat(component.amount) || 0;
                const compTotalPrice = compUnitPrice * compQuantity;
                const compAmountWithVat =
                  (parseFloat(component.amountWithVat) || 0) * compQuantity;

                await itemService.createProjectItem(
                  new CreateProjectItemRequest(
                    store.Server,
                    store.SessionId,
                    generateProjectProductItem(
                      record.folderId || "",
                      record.projectId || "",
                      record.activityId || "",
                      component.component,
                      component.componentName,
                      compQuantity,
                      compUnitPrice,
                      compTotalPrice,
                      compAmountWithVat,
                      component.currencyName || "",
                      String(component.vatValue || 0),
                      String(component.purchasePrice || 0),
                      "", // attrib0
                      "", // attrib0_id
                      "", // attrib1
                      "", // attrib1_id
                      "", // attrib2
                      "", // attrib2_id
                      "", // attrib3
                      "", // attrib3_id
                      "", // attrib4
                      "", // attrib4_id
                      "", // attrib5
                      "", // attrib5_id
                      "", // attrib6
                      "", // attrib6_id
                      "", // attrib7
                      "", // attrib7_id
                      "", // attrib8
                      "", // attrib8_id
                      "", // attrib9
                      "", // attrib9_id
                      "",
                      "",
                      billingDate,
                      "1",
                      parentItemId,
                      { type: String(productType) },
                    ),
                  ),
                );
              } catch (compError) {
                console.error(
                  `Error saving component "${component.componentName}":`,
                  compError,
                );
              }
            }
          }

          successCount++;
        } catch (productError) {
          failureCount++;
          failedProducts.push(product.productName);
          console.error(
            `Error adding product "${product.productName}":`,
            productError,
          );
        }
      });

      await Promise.all(productPromises);

      setIsProductSelectionModalOpen(false);

      if (successCount > 0 && failureCount === 0) {
        ToastQueue.positive(
          t("item(s)_added_successfully", {
            ns: "layout_components",
            defaultValue: "Item(s) added successfully",
          }).replace("Item(s)", String(successCount)),
          {
            timeout: 3000,
          },
        );
      } else if (successCount > 0 && failureCount > 0) {
        ToastQueue.info(
          `${successCount} ${t("item(s)_added", {
            ns: "layout_components",
            defaultValue: "item(s) added",
          })}, ${failureCount} ${t("failed", {
            ns: "layout_components",
            defaultValue: "Failed",
          }).toLowerCase()}: ${failedProducts.join(", ")}`,
          { timeout: 5000 },
        );
      } else {
        ToastQueue.negative(
          `${t("failed_to_add_items", {
            ns: "layout_components",
            defaultValue: "Failed to add items",
          })}: ${failedProducts.join(", ")}`,
          { timeout: 5000 },
        );
      }

      await loadProjectProductData();
    } catch (error: any) {
      console.error("Error in product save:", error);
      ToastQueue.negative(
        error.message ||
          t("error_adding_items", {
            ns: "layout_components",
            defaultValue: "Error adding items",
          }),
        {
          timeout: 3000,
        },
      );
    } finally {
      isSavingProducts.current = false;
      setShowLoader(false);
    }
  };

  const addRecord = async (record: any, parentId?: string | null) => {
    try {
      if (isNew && setData) {
        const updatedRow: ProjectProductTreeRowData = {
          ...record,
          id: Math.random().toString(36).substring(7),
          unitPriceCurr:
            formatDecimalToString(record.unitPrice) + " " + record.currency,
          billingDateFormatted: moment(record.billingDate).format("MM/DD/YYYY"),
          parentId: null,
          childrenIds: [],
          depth: 0,
          isExpanded: true,
        };

        const newRowsData = [...rowsData, updatedRow];
        const withTreeMeta = buildTreeMetadata(newRowsData);
        setRowsData(withTreeMeta);
        setData(withTreeMeta);
        handleRowClose();
        return;
      }
      setShowLoader(true);
      setRecordIds(record, selectedItem);
      console.log("record", record);
      const item = generateProjectProductItem(
        record.folderId,
        record.projectId,
        record.activityId,
        record.partID,
        record.productName,
        record.quantity,
        record.unitPrice,
        record.totalPrice,
        record.totalAmountWithVat || 0,
        record.currency,
        record.vatValue || 0,
        record.purchasePrice,
        record.attrib0,
        record.attrib0_id,
        record.attrib1,
        record.attrib1_id,
        record.attrib2,
        record.attrib2_id,
        record.attrib3,
        record.attrib3_id,
        record.attrib4,
        record.attrib4_id,
        record.attrib5,
        record.attrib5_id,
        record.attrib6,
        record.attrib6_id,
        record.attrib7,
        record.attrib7_id,
        record.attrib8,
        record.attrib8_id,
        record.attrib9,
        record.attrib9_id,
        record.supplier,
        record.refId,
        record.billingDate,
        "1",
        parentId ?? undefined,
        { type: String(productType) },
      );
      item.itemSerial = record.itemSerial ?? "";
      const result = await itemService.createProjectItem(
        new CreateProjectItemRequest(store.Server, store.SessionId, item),
      );

      await handleResult(
        result,
        t("record_saved_successfully", { ns: "layout_components" }),
      );
    } catch (error: any) {
      ToastQueue.negative(error.message, { timeout: 3000 });
    } finally {
      setShowLoader(false);
    }
  };

  const updateRecord = async (record: any) => {
    try {
      if (isNew && setData) {
        record.unitPriceCurr =
          formatDecimalToString(record.unitPrice) + " " + record.currency;
        record.billingDateFormatted = moment(record.billingDate).format(
          "MM/DD/YYYY",
        );
        const updatedRowsData = rowsData.map((row) =>
          row.id === record.id ? { ...row, ...record } : row,
        );
        const withTreeMeta = buildTreeMetadata(updatedRowsData);
        setRowsData(withTreeMeta);
        setData(withTreeMeta);
        handleRowClose();
        return;
      }
      setShowLoader(true);
      const itemRow = projectProductData.ROWS.find(
        (row: any) => row.ITEM.id === record.id,
      );
      setRecordIds(record, selectedItem);
      const parentIdToSend =
        (record.parentId != null ? record.parentId : itemRow?.ITEM?.parentId) ??
        undefined;
      const item = generateProjectProductItem(
        record.folderId,
        record.projectId,
        record.activityId,
        record.partID,
        record.productName,
        record.quantity,
        record.unitPrice,
        record.totalPrice,
        record.totalAmountWithVat || 0,
        record.currency,
        record.vatValue || 0,
        record.purchasePrice,
        record.attrib0,
        record.attrib0_id,
        record.attrib1,
        record.attrib1_id,
        record.attrib2,
        record.attrib2_id,
        record.attrib3,
        record.attrib3_id,
        record.attrib4,
        record.attrib4_id,
        record.attrib5,
        record.attrib5_id,
        record.attrib6,
        record.attrib6_id,
        record.attrib7,
        record.attrib7_id,
        record.attrib8,
        record.attrib8_id,
        record.attrib9,
        record.attrib9_id,
        record.supplier,
        record.refId,
        record.billingDate,
        "1",
        parentIdToSend,
        itemRow.ITEM,
      );
      if (record.sortOrder != null) {
        item.orderIndex = String(record.sortOrder);
      }
      item.itemSerial = record.itemSerial ?? itemRow?.ITEM?.itemSerial ?? "";
      const result = await itemService.updateProjectItem(
        new UpdateProjectItemRequest(store.Server, store.SessionId, item),
      );

      await handleResult(
        result,
        t("record_updated_successfully", { ns: "layout_components" }),
      );
    } catch (error: any) {
      ToastQueue.negative(error.message, { timeout: 3000 });
    } finally {
      setShowLoader(false);
    }
  };

  const deleteRecord = async (record: any) => {
    try {
      if (isNew && setData) {
        const updatedRowsData = rowsData.filter((row) => row.id !== record.id);
        const withTreeMeta = buildTreeMetadata(updatedRowsData);
        setRowsData(withTreeMeta);
        setData(withTreeMeta);
        handleRowClose();
        return;
      }
      setShowLoader(true);

      const result = await itemService.deleteProjectItem(
        new DeleteProjectItemRequest(store.Server, store.SessionId, record.id),
      );
      await handleResult(
        result,
        t("record_deleted_successfully", { ns: "layout_components" }),
      );
    } catch (error) {
      console.log("deleteRecord Error", error);
    } finally {
      setShowLoader(false);
    }
  };

  const handleResult = async (result: any, successMessage: string) => {
    if (result.result === "OK") {
      await loadProjectProductData();
      handleRowClose();
      ToastQueue.positive(successMessage, { timeout: 3000 });
    } else {
      ToastQueue.negative(result.EXCEPTION.reason, { timeout: 3000 });
    }
  };

  const closeDialog = () => {
    setIsOpen(false);
  };

  const getNewRowDefaultData = (): Partial<ProjectProductTreeRowData> => {
    const currency = rowsData[0]?.currency ?? "";
    return {
      ...defaultRowData,
      quantity: 1,
      unitPrice: 0,
      amountWithVat: 0,
      totalPrice: "0",
      totalPriceWithVat: "0",
      totalPriceCurr: "0 " + currency,
      unitPriceCurr: "0 " + currency,
      attrib0: "",
      attrib0_id: "",
      attrib1: "",
      attrib1_id: "",
      attrib2: "",
      attrib2_id: "",
      refId: "",
      itemSerial: "",
      billingDate: moment().toISOString(),
      billingDateFormatted: moment().format("MM/DD/YYYY"),
    };
  };

  const handleRowUpdate = (
    updatedRow: TreeGridRow<ProjectProductTreeRowData>,
  ) => {
    const data = { ...updatedRow.data } as ProjectProductTreeRowData;
    const qty = data.quantity ?? 0;
    const unitPrice = data.unitPrice ?? 0;
    const amountWithVat = data.amountWithVat ?? 0;
    const currency = data.currency ?? "";
    data.totalPrice = (qty * unitPrice).toFixed(2);
    data.totalPriceWithVat = (qty * amountWithVat).toFixed(2);
    data.unitPriceCurr =
      formatDecimalToString(String(unitPrice)) + " " + currency;
    data.totalPriceCurr =
      formatDecimalToString(data.totalPrice) + " " + currency;

    if (updatedRow.id.startsWith("row_")) {
      const record = { ...data, parentId: updatedRow.parentId ?? undefined };
      addRecord(record, record.parentId).then(() =>
        setTreeGridKey((k) => k + 1),
      );
    } else {
      updateRecord(data);
    }
  };

  const handleInfoClick = (
    rowId: string,
    row: TreeGridRow<ProjectProductTreeRowData>,
  ) => {
    const dataRow =
      rowsData.find((r) => r.id === rowId) ||
      (row.data as ProjectProductTreeRowData);
    setSelectedRow(dataRow);
    setShowAddEditRow(true);
    if (isMobile) {
      setIsOpen(true);
    }
  };

  const handleDeleteClick = async (
    rowId: string,
    row: TreeGridRow<ProjectProductTreeRowData>,
  ) => {
    const dataRow =
      rowsData.find((r) => r.id === rowId) ||
      (row.data as ProjectProductTreeRowData);
    await deleteRecord(dataRow);
  };

  const handleRowMove = (
    rowId: string,
    newParentId: string | null,
    newIndex: number,
  ) => {
    const rows: TreeGridRow<ProjectProductTreeRowData>[] = rowsData.map(
      (item, index) => ({
        id: String(item.id || `row-${index}`),
        parentId:
          item.parentId &&
          rowsData.find((r) => String(r.id) === String(item.parentId))
            ? String(item.parentId)
            : null,
        depth: item.depth ?? 0,
        childrenIds: item.childrenIds ?? [],
        isExpanded: item.isExpanded !== false,
        rowType: "product" as const,
        sortOrder: item.sortOrder ?? index,
        data: item,
      }),
    );
    const copy = rows.map((r) => ({
      ...r,
      data: { ...r.data },
      childrenIds: [...(r.childrenIds || [])],
    }));
    const result = moveRow(rowId, newParentId, newIndex, copy);
    if (!result.success) return;
    const flatItems: ProjectProductTreeRowData[] = copy.map((r) => ({
      ...r.data,
      id: r.id,
      parentId: r.parentId,
      childrenIds: r.childrenIds || [],
      sortOrder: r.sortOrder ?? 0,
    }));
    setRowsData(buildTreeMetadata(flatItems));
    const movedRow = flatItems.find((i) => i.id === rowId);
    if (movedRow) updateRecord(movedRow);
  };

  if (showLoader) {
    return (
      <Flex width="100%" justifyContent={"center"} marginTop={10}>
        <ProgressCircle aria-label="Loading…" isIndeterminate />
      </Flex>
    );
  }

  const treeGridRows: TreeGridRow<ProjectProductTreeRowData>[] = rowsData.map(
    (item, index) => ({
      id: String(item.id || `row-${index}`),
      parentId:
        item.parentId &&
        rowsData.find((r) => String(r.id) === String(item.parentId))
          ? String(item.parentId)
          : null,
      depth: item.depth ?? 0,
      childrenIds: item.childrenIds ?? [],
      isExpanded: item.isExpanded !== false,
      rowType: "product",
      sortOrder: item.sortOrder ?? index,
      data: item,
    }),
  );

  return (
    <View position={"relative"} width={"100%"} minHeight={minHeight}>
      {/* Heading and Add button */}
      <View position={"relative"} width={"100%"}>
        <Flex direction={"column"} gap={"size-150"} width={"100%"}>
          <Flex
            direction={"row"}
            alignItems={"start"}
            justifyContent={"start"}
            UNSAFE_className={"heading_text_parent"}
          >
            <Content
              position={"relative"}
              UNSAFE_className={styles.heading_text}
              UNSAFE_style={{
                border:
                  isRequired && rowsData.length === 0
                    ? "1px solid red"
                    : undefined,
                borderRadius: "5px",
                padding: "10px",
                width: "auto",
              }}
            >
              {componentTitle}
            </Content>
            {isRequired && rowsData.length === 0 && (
              <Flex
                direction={"row"}
                UNSAFE_style={{
                  color: "red",
                  fontSize: "10px",
                  padding: "15px",
                }}
              >
                {componentTitle.toLocaleLowerCase()} required
              </Flex>
            )}
            <Flex
              direction={"row"}
              alignItems={"center"}
              justifyContent={"center"}
              gap={"size-100"}
              UNSAFE_className={styles.icon_add_parent}
            >
              <Content>
                <Link
                  isQuiet
                  onPress={() => {
                    setIsProductSelectionModalOpen(true);
                  }}
                >
                  <i className="bi bi-plus fs-5">
                    <View UNSAFE_className={styles.icon_add_text}>
                      {t("add", { ns: "layout_components" })}
                    </View>
                  </i>
                </Link>
              </Content>
            </Flex>
          </Flex>

          {/* TreeGrid section */}
          <Flex direction={"column"} width={"100%"} UNSAFE_style={tableStyles}>
            <AttributesCacheContext.Provider value={attributesCache}>
              <Flex height={containerHeight}>
                <TreeGridComponent<ProjectProductTreeRowData>
                  key={treeGridKey}
                  title=""
                  columns={columns}
                  data={treeGridRows}
                  showSelection={true}
                  showSelectAll={true}
                  enableDragDrop={true}
                  enableIndentOutdent={true}
                  enableInlineEdit={true}
                  enableKeyboardNav={true}
                  enableKeyboardCreate={false}
                  showToolbar={false}
                  showGridLines={true}
                  striped={false}
                  showInfoIcon={true}
                  showDeleteIcon={true}
                  onInfoClick={handleInfoClick}
                  onDeleteClick={handleDeleteClick}
                  showDeleteConfirmation={true}
                  maxHeight={tableHeight}
                  onRowUpdate={handleRowUpdate}
                  onRowMove={handleRowMove}
                  getNewRowDefaultData={getNewRowDefaultData}
                  onRowCreate={() => {
                    return false;
                  }}
                />
              </Flex>
            </AttributesCacheContext.Provider>

            {/* Mobile Dialog Container inside table area */}
            {isMobile && (
              <DialogContainer
                isDismissable
                onDismiss={() => {
                  setIsOpen(false);
                }}
              >
                {isOpen && (
                  <Dialog>
                    <Heading>
                      {selectedRow.id === "0"
                        ? t("add", { ns: "layout_components" })
                        : t("edit", { ns: "layout_components" })}
                    </Heading>
                    <Content>
                      <ProjectProductAddEdit
                        id={String(selectedItem?.id)}
                        selectedItemType={selectedItem?.type}
                        defaultRowData={defaultRowData}
                        selectedRowData={selectedRow}
                        handleRowClose={handleRowClose}
                        addRecord={addRecord}
                        updateRecord={updateRecord}
                        deleteRecord={deleteRecord}
                        attrib0ColumnName={attrib0ColumnName}
                        attrib1ColumnName={attrib1ColumnName}
                        attrib2ColumnName={attrib2ColumnName}
                        attrib0ColumnVisible={attrib0ColumnVisible}
                        attrib1ColumnVisible={attrib1ColumnVisible}
                        attrib2ColumnVisible={attrib2ColumnVisible}
                        attributesData={
                          getAttributes(selectedRow.itemType) ?? []
                        }
                        billingDateColumnName={billingDateColumnName}
                        billingDateColumnVisible={billingDateColumnVisible}
                        componentTitle={componentTitle}
                        quantityColumnName={quantityColumnName}
                        quantityColumnVisible={quantityColumnVisible}
                        serialNumberColumnName={serialNumberColumnName}
                        serialNumberColumnVisible={serialNumberColumnVisible}
                        unitPriceColumnName={unitPriceColumnName}
                        unitPriceColumnVisible={unitPriceColumnVisible}
                        supplierColumnVisible={supplierColumnVisible}
                        closeDialog={closeDialog}
                        styles={styles}
                        onLoadAttributes={handleLoadAttributes}
                      />
                    </Content>
                  </Dialog>
                )}
              </DialogContainer>
            )}
          </Flex>
        </Flex>
      </View>

      {/* Desktop AddEdit section */}
      {!isMobile && showAddEditRow && (
        <Flex
          direction={"column"}
          width={"100%"}
          marginTop={"size-200"}
          UNSAFE_style={{ zIndex: 2 }}
        >
          <ProjectProductAddEdit
            id={String(selectedItem?.id)}
            selectedItemType={selectedItem?.type}
            defaultRowData={defaultRowData}
            selectedRowData={selectedRow}
            handleRowClose={handleRowClose}
            addRecord={addRecord}
            updateRecord={updateRecord}
            deleteRecord={deleteRecord}
            attrib0ColumnName={attrib0ColumnName}
            attrib1ColumnName={attrib1ColumnName}
            attrib2ColumnName={attrib2ColumnName}
            attrib0ColumnVisible={attrib0ColumnVisible}
            attrib1ColumnVisible={attrib1ColumnVisible}
            attrib2ColumnVisible={attrib2ColumnVisible}
            attributesData={getAttributes(selectedRow.itemType) ?? []}
            billingDateColumnName={billingDateColumnName}
            billingDateColumnVisible={billingDateColumnVisible}
            componentTitle={componentTitle}
            quantityColumnName={quantityColumnName}
            quantityColumnVisible={quantityColumnVisible}
            serialNumberColumnName={serialNumberColumnName}
            serialNumberColumnVisible={serialNumberColumnVisible}
            unitPriceColumnName={unitPriceColumnName}
            unitPriceColumnVisible={unitPriceColumnVisible}
            supplierColumnVisible={supplierColumnVisible}
            closeDialog={closeDialog}
            styles={styles}
            onLoadAttributes={handleLoadAttributes}
          />
        </Flex>
      )}

      {/* Product Selection Modal */}
      <ProductSelectionModal
        isOpen={isProductSelectionModalOpen}
        onClose={() => setIsProductSelectionModalOpen(false)}
        onSave={handleSaveSelectedProducts}
      />
    </View>
  );
}

export const ProjectProductTreeComponent = LayoutComponent(ProjectProductTree);
